/**
 * Document title for the SPA.
 *
 * The tab title is composed of two halves that move independently:
 *   - the page half, pushed by the router from `meta.title` on every navigation
 *   - the app half, the configured company name, which only arrives once the
 *     configuration store has loaded (and changes when an admin renames it)
 *
 * Keeping them apart means a late-arriving company name re-renders the tab
 * without the router having to navigate again, and vice versa.
 */

const DEFAULT_APP_NAME = 'Inventree';
const SEPARATOR = ' · ';

let pageTitle = '';
// The blade shell renders the configured company name as the whole title, so
// the server-rendered value seeds the app half — no flash of the fallback name
// while the configuration store is still loading.
let appName = (typeof document !== 'undefined' && document.title.trim()) || DEFAULT_APP_NAME;

function render() {
    if (typeof document === 'undefined') return;
    document.title = pageTitle ? `${pageTitle}${SEPARATOR}${appName}` : appName;
}

/**
 * Read a route's `meta.title`. It may be a plain string, or a function of the
 * route for pages whose title depends on params (`Sale #12`).
 * @param {import('vue-router').RouteLocationNormalized} route
 * @returns {string} '' when the route declares no title
 */
export function resolveRouteTitle(route) {
    const title = route?.meta?.title;
    if (typeof title === 'function') {
        return String(title(route) ?? '');
    }
    return typeof title === 'string' ? title : '';
}

/** Set the page half of the tab title. Empty string leaves just the app name. */
export function setPageTitle(title) {
    pageTitle = typeof title === 'string' ? title.trim() : '';
    render();
}

/** Set the app half of the tab title (the configured company name). */
export function setAppName(name) {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    appName = trimmed || DEFAULT_APP_NAME;
    render();
}
